import React from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';

const Services: React.FC = () => {
  const services = [
    {
      title: 'Office Design Consultation',
      description: 'Expert space planning and design services to optimize your office layout for productivity and aesthetics.',
      features: ['Space Planning', '3D Visualization', 'Ergonomic Assessment', 'Brand Integration'],
      icon: 'ri-pencil-ruler-2-line',
      image: 'https://public.readdy.ai/ai/img_res/5ff88643d26d6d3550a92ca4c372d42b.jpg'
    },
    {
      title: 'Furniture Delivery',
      description: 'Reliable and secure delivery services across South Carolina and surrounding states.',
      features: ['White Glove Service', 'Scheduled Delivery', 'Damage Protection', 'Regional Coverage'],
      icon: 'ri-truck-line',
      image: 'https://readdy.ai/api/search-image?query=Professional%20delivery%20team%20carefully%20moving%20office%20furniture%20from%20truck%20to%20commercial%20building%2C%20wearing%20uniforms%20and%20using%20proper%20equipment%2C%20modern%20delivery%20truck%20with%20POG%20branding&width=600&height=400&seq=delivery-service-detail&orientation=landscape'
    },
    {
      title: 'Professional Installation',
      description: 'Complete assembly and installation services ensuring your furniture is ready for immediate use.',
      features: ['Expert Assembly', 'Quality Testing', 'Cleanup Service', 'Warranty Support'],
      icon: 'ri-tools-line',
      image: 'https://readdy.ai/api/search-image?query=Skilled%20technicians%20assembling%20office%20furniture%20in%20a%20modern%20commercial%20space%2C%20using%20professional%20tools%20and%20equipment%2C%20clean%20organized%20workspace%20with%20partially%20assembled%20desks%20and%20chairs&width=600&height=400&seq=installation-service-detail&orientation=landscape'
    },
    {
      title: 'Project Management',
      description: 'End-to-end project coordination for large-scale office furniture installations.',
      features: ['Timeline Management', 'Coordination', 'Quality Control', 'Progress Reporting'],
      icon: 'ri-file-list-3-line',
      image: 'https://readdy.ai/api/search-image?query=Project%20manager%20coordinating%20office%20furniture%20installation%2C%20reviewing%20plans%20and%20schedules%20in%20a%20modern%20office%20environment%20with%20charts%2C%20timelines%2C%20and%20communication%20devices&width=600&height=400&seq=project-management-service&orientation=landscape'
    },
    {
      title: 'Maintenance & Support',
      description: 'Ongoing maintenance and support services to keep your office furniture in optimal condition.',
      features: ['Regular Maintenance', 'Repair Services', 'Replacement Parts', '24/7 Support'],
      icon: 'ri-customer-service-2-line',
      image: 'https://readdy.ai/api/search-image?query=Professional%20maintenance%20technician%20servicing%20office%20furniture%2C%20adjusting%20and%20cleaning%20chairs%20and%20desks%20in%20a%20modern%20office%20setting%20with%20tools%20and%20cleaning%20supplies&width=600&height=400&seq=maintenance-service&orientation=landscape'
    },
    {
      title: 'Space Reconfiguration',
      description: 'Flexible solutions for office layout changes and furniture reconfiguration needs.',
      features: ['Layout Redesign', 'Furniture Moving', 'Space Optimization', 'Minimal Disruption'],
      icon: 'ri-layout-grid-line',
      image: 'https://readdy.ai/api/search-image?query=Office%20space%20being%20reconfigured%20with%20furniture%20being%20moved%20and%20rearranged%2C%20professional%20team%20working%20efficiently%20in%20a%20modern%20commercial%20office%20environment&width=600&height=400&seq=reconfiguration-service&orientation=landscape'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Comprehensive <strong>Office Furniture</strong> Services
          </h1>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            From initial design consultation to ongoing maintenance, Professional Office Group 
            provides complete office furniture solutions across South Carolina.
          </p>
          <Button variant="secondary" size="lg" href="/quote">
            <i className="ri-calculator-line mr-2"></i>
            Get Service Quote
          </Button>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {services.map((service, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
                <div className="h-64 bg-cover bg-center object-top" style={{ backgroundImage: `url('${service.image}')` }}></div>
                <div className="p-8">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                      <i className={`${service.icon} text-2xl text-blue-600`}></i>
                    </div>
                    <h3 className="text-2xl font-semibold text-gray-900">{service.title}</h3>
                  </div>
                  <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                  <div className="grid grid-cols-2 gap-2 mb-6">
                    {service.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center text-sm text-gray-600">
                        <i className="ri-check-line text-green-500 mr-2"></i>
                        {feature}
                      </div>
                    ))}
                  </div>
                  <Button variant="outline" size="sm" href="/contact">
                    Learn More
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our <strong>Service Process</strong>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We follow a proven process to ensure your office furniture project is completed 
              efficiently and exceeds your expectations.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Consultation', description: 'Initial meeting to understand your needs and requirements' },
              { step: '02', title: 'Design', description: 'Custom design and space planning tailored to your business' },
              { step: '03', title: 'Delivery', description: 'Professional delivery and installation at your location' },
              { step: '04', title: 'Support', description: 'Ongoing maintenance and customer support services' }
            ].map((process, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-orange-500 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                  {process.step}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{process.title}</h3>
                <p className="text-gray-600">{process.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Contact Professional Office Group today to discuss your office furniture needs 
            and receive a customized service proposal.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button variant="secondary" size="lg" href="/quote">
              <i className="ri-calculator-line mr-2"></i>
              Request Quote
            </Button>
            <Button variant="outline" size="lg" href="/contact" className="border-white text-white hover:bg-white hover:text-blue-600">
              <i className="ri-phone-line mr-2"></i>
              Contact Us
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Services;